import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { keyframes } from '@emotion/react';
import Fab from '@mui/material/Fab';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useAuth } from '../../auth/AuthContext';
import { useProjects } from '../../context/ProjectContext';
import { chatbotApi, featureRequestApi, featureRequestFileApi, phaseApi } from '../../api/endpoints';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../Notifier';
import { ImageUploader } from '../ImageUploader';
import { RequestFormDialog } from '../../pages/FeatureRequests/RequestFormDialog';
import { useChatDraft } from './useChatDraft';
import { useAiConversation } from './useAiConversation';
import { getFallbackStep, getNextStep } from './chatEngine';
import type { AiTurn, ChatDraft } from './chatSteps';
import type { ChatbotConfig, Phase } from '../../types';
import type { GuideTopic } from './guideContent';
import { GUIDE_TOPIC_OPTIONS, GUIDE_TOPIC_STEPS } from './guideContent';

const REFINE_WAIT_TIMEOUT_MS = 3000;
const WINDOW_WIDTH = 360;
const WINDOW_HEIGHT = 540;
const FAB_SIZE = 56;
/** Khoảng hở giữa nút nổi và cửa sổ chat, và lề tối thiểu với mép màn hình. */
const FAB_GAP = 12;
const VIEWPORT_EDGE = 8;
/** Mặc định nằm góc dưới-trái. */
const DEFAULT_FAB_POSITION = { x: VIEWPORT_EDGE + 16, y: window.innerHeight - FAB_SIZE - 24 };
/** Coi là "kéo" (không phải "bấm") khi con trỏ đi quá ngưỡng này. */
const DRAG_THRESHOLD_PX = 4;

function clampToViewport(x: number, y: number, width: number, height: number) {
  const maxX = Math.max(window.innerWidth - width - VIEWPORT_EDGE, VIEWPORT_EDGE);
  const maxY = Math.max(window.innerHeight - height - VIEWPORT_EDGE, VIEWPORT_EDGE);
  return { x: Math.min(Math.max(x, VIEWPORT_EDGE), maxX), y: Math.min(Math.max(y, VIEWPORT_EDGE), maxY) };
}

/**
 * Cửa sổ chat neo vào nút nổi: mép phải thẳng hàng mép phải của nút và nằm
 * ngay phía trên nút, nên kéo nút đi đâu thì cửa sổ theo đó.
 */
function windowPositionFor(fab: { x: number; y: number }) {
  return clampToViewport(fab.x + FAB_SIZE - WINDOW_WIDTH, fab.y - WINDOW_HEIGHT - FAB_GAP, WINDOW_WIDTH, WINDOW_HEIGHT);
}
/** Độ trễ giả lập "đang nhập" trước khi bong bóng của bot hiện ra — giống Messenger. */
const BOT_TYPING_DELAY_MS = 500;
const BOT_NAME = 'Allianceitsc';

const messageInAnimation = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const typingBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
`;

/** Các bước nhận câu trả lời bằng cách gõ chữ — những bước còn lại chỉ bấm lựa chọn. */
const TEXT_INPUT_STEPS = ['ASK_TITLE', 'ASK_DESCRIPTION', 'AI_CONVERSE'] as const;

/** Một dòng trong lịch sử hội thoại — chỉ hiển thị, không ảnh hưởng state machine. */
interface ChatMessage {
  id: number;
  role: 'bot' | 'user';
  text: string;
  time: string;
}

function nowLabel(): string {
  return new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

export function ChatbotWidget(): JSX.Element | null {
  const { user, hasPermission } = useAuth();
  const { projects } = useProjects();
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState<ChatbotConfig | null>(null);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingEntry, setConfirmingEntry] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [botTyping, setBotTyping] = useState(false);
  /** Sửa trực tiếp ngay trên thẻ CONFIRM — không rời step, không hỏi lại qua chat. */
  const [editingField, setEditingField] = useState<'title' | 'description' | 'project' | null>(null);
  const [editValue, setEditValue] = useState('');
  /** Bấm "Để tôi tự mô tả" ở CHOOSE_MODE: đóng cửa sổ chat, mở thẳng form thủ công. */
  const [manualFormOpen, setManualFormOpen] = useState(false);
  /**
   * Chế độ của cửa sổ chat — hoàn toàn tách biệt với luồng tạo yêu cầu chức
   * năng (step/draft ở trên): 'feature-request' là luồng mặc định/cũ,
   * 'guide' là màn hình hướng dẫn sử dụng thuần văn bản (không gọi API, không
   * đụng tới `draft`). Hai chế độ dùng chung khung cửa sổ chat (Fab, kéo
   * thả, header) nhưng render nội dung thân bài hoàn toàn khác nhau.
   */
  const [widgetMode, setWidgetMode] = useState<'feature-request' | 'guide'>('feature-request');
  /** null = đang ở màn hình chọn chủ đề hướng dẫn; có giá trị = đang xem từng bước của chủ đề đó. */
  const [guideTopic, setGuideTopic] = useState<GuideTopic | null>(null);
  const [guideStepIndex, setGuideStepIndex] = useState(0);
  const [fabPosition, setFabPosition] = useState(DEFAULT_FAB_POSITION);
  const dragStateRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);
  /** Phân biệt "kéo" với "bấm": sau khi kéo, cú click kèm theo không được bật/tắt cửa sổ. */
  const draggedRef = useRef(false);
  const titleRefinePromise = useRef<Promise<string> | null>(null);
  const descriptionRefinePromise = useRef<Promise<string> | null>(null);
  const messageIdRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const openImagePickerRef = useRef<(() => void) | null>(null);

  const { step, setStep, draft, setDraft, reset } = useChatDraft(user?.id ?? null);
  const aiConversation = useAiConversation();

  const canUseChatbot = hasPermission('FEATURE_REQUEST_CREATE');

  /**
   * Kéo cả khối (nút nổi + cửa sổ chat) bằng chuột — dùng chung cho nút nổi và
   * thanh tiêu đề. Nguồn sự thật là vị trí NÚT; cửa sổ luôn được tính ra từ đó
   * nên hai thứ không bao giờ lệch nhau.
   */
  const handleDragStart = (e: ReactMouseEvent<HTMLElement>) => {
    dragStateRef.current = { startX: e.clientX, startY: e.clientY, originX: fabPosition.x, originY: fabPosition.y };
    draggedRef.current = false;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const dx = moveEvent.clientX - dragState.startX;
      const dy = moveEvent.clientY - dragState.startY;
      if (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX) draggedRef.current = true;
      setFabPosition(clampToViewport(dragState.originX + dx, dragState.originY + dy, FAB_SIZE, FAB_SIZE));
    };
    const handleMouseUp = () => {
      dragStateRef.current = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  /** Bấm nút nổi: bật/tắt cửa sổ — trừ khi cú bấm đó thực ra là kết thúc một lần kéo. */
  const handleFabClick = () => {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    setOpen((v) => !v);
  };

  // Cửa sổ trình duyệt đổi kích thước có thể đẩy nút ra ngoài vùng nhìn thấy.
  useEffect(() => {
    const handleResize = () => setFabPosition((prev) => clampToViewport(prev.x, prev.y, FAB_SIZE, FAB_SIZE));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const registerOpenPicker = useCallback((openPicker: () => void) => {
    openImagePickerRef.current = openPicker;
  }, []);

  useEffect(() => {
    if (!open || !canUseChatbot || config) return;
    void chatbotApi
      .getConfig()
      .then(setConfig)
      .catch((err) => notify(getApiErrorMessage(err, 'Không thể tải cấu hình chatbot'), 'error'));
  }, [open, canUseChatbot, config]);

  useEffect(() => {
    if (!draft.projectId) {
      setPhases([]);
      return;
    }
    let active = true;
    phaseApi
      .listByProject(draft.projectId, { limit: 200 })
      .then((result) => {
        if (active) setPhases(result.items);
      })
      .catch(() => {
        if (active) setPhases([]);
      });
    return () => {
      active = false;
    };
  }, [draft.projectId]);

  const pushMessage = (role: ChatMessage['role'], text: string) => {
    if (!text) return;
    messageIdRef.current += 1;
    setMessages((prev) => [...prev, { id: messageIdRef.current, role, text, time: nowLabel() }]);
  };

  const pushBot = (text: string) => pushMessage('bot', text);
  const pushUser = (text: string) => pushMessage('user', text);

  /**
   * Hiện chấm "đang nhập" trong BOT_TYPING_DELAY_MS rồi mới đẩy tin nhắn thật —
   * giống hiệu ứng gõ tin nhắn của Messenger, thay vì bong bóng bật ra ngay lập tức.
   */
  const pushBotDelayed = (text: string, after?: () => void) => {
    setBotTyping(true);
    setTimeout(() => {
      pushBot(text);
      setBotTyping(false);
      after?.();
    }, BOT_TYPING_DELAY_MS);
  };

  /**
   * Câu chào mở đầu — đẩy khi config đã tải xong, cửa sổ đang mở, và lịch sử
   * còn trống. Phụ thuộc cả `open` (không chỉ `config`): sau khi "Đóng" ở
   * bước DONE xoá sạch `messages` rồi đóng cửa sổ, mở lại cửa sổ không làm
   * `config` đổi tham chiếu (đã tải từ lần mở trước) nên cần `open` để effect
   * chạy lại, nếu không user sẽ không bao giờ thấy lời chào lần thứ hai trở đi.
   */
  useEffect(() => {
    if (open && config && messages.length === 0) {
      pushBotDelayed(config.prompts.GREETING ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, open]);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, step, confirmingEntry, botTyping]);

  if (!canUseChatbot) return null;

  const promptFor = (key: string): string => config?.prompts[key] ?? '';
  const aiEnabled = (config?.ai_enabled ?? 0) === 1;

  const goTo = (next: ReturnType<typeof getNextStep>) => setStep(next);

  const handleSelectService = (svc: ChatbotConfig['services'][number]) => {
    pushUser(svc.label);
    const next = { ...draft, service: svc.key };
    setDraft(next);
    const nextStep = getNextStep('SELECT_SERVICE', next);
    pushBotDelayed(nextStep === 'CHOOSE_MODE' ? promptFor('CHOOSE_MODE') : promptFor('ASK_TITLE'), () =>
      goTo(nextStep),
    );
  };

  /** CHOOSE_MODE: user tự chọn "Tôi tự mô tả" (AI) hay "Hướng dẫn tôi từng bước" (rule-based). */
  const handleChooseMode = (mode: 'ai' | 'steps') => {
    pushUser(mode === 'ai' ? 'Trò chuyện với AI' : 'Hướng dẫn tôi từng bước');
    const next = { ...draft, mode };
    setDraft(next);
    const nextStep = getNextStep('CHOOSE_MODE', next);
    pushBotDelayed(nextStep === 'AI_CONVERSE' ? promptFor('AI_INTRO') : promptFor('ASK_TITLE'), () =>
      goTo(nextStep),
    );
  };

  /** "Để tôi tự mô tả" — bỏ qua hỏi đáp, đóng cửa sổ chat và mở thẳng form nhập tự do. */
  const handleOpenManualForm = () => {
    pushUser('Để tôi tự mô tả');
    setOpen(false);
    setManualFormOpen(true);
  };

  const handleSubmitTitle = () => {
    if (textInput.trim().length === 0) return;
    const rawTitle = textInput.trim();
    pushUser(rawTitle);
    titleRefinePromise.current = chatbotApi.refine('title', rawTitle).catch(() => rawTitle);
    setDraft((prev) => ({ ...prev, rawTitle }));
    setTextInput('');
    pushBotDelayed(promptFor('ASK_DESCRIPTION'), () => goTo(getNextStep('ASK_TITLE', { ...draft, rawTitle })));
  };

  const handleSubmitDescription = () => {
    if (textInput.trim().length === 0) return;
    const rawDescription = textInput.trim();
    pushUser(rawDescription);
    descriptionRefinePromise.current = chatbotApi.refine('description', rawDescription).catch(() => rawDescription);
    setDraft((prev) => ({ ...prev, rawDescription }));
    setTextInput('');
    pushBotDelayed(promptFor('ASK_PROJECT'), () =>
      goTo(getNextStep('ASK_DESCRIPTION', { ...draft, rawDescription })),
    );
  };

  /**
   * Rơi về luồng hỏi-từng-bước: set mode='steps', nhảy tới bước đầu tiên còn
   * thiếu dữ liệu qua `getFallbackStep` (không bắt user nhập lại cái đã có).
   * Dùng chung cho 2 trường hợp: backend tự quyết (exhausted/unavailable, có
   * `draftOverride` từ AI) và user chủ động bấm "Dừng" (không có override).
   */
  const fallbackToSteps = (draftOverride?: Partial<ChatDraft>) => {
    const fallbackDraft: ChatDraft = { ...draft, ...draftOverride, mode: 'steps' };
    setDraft(fallbackDraft);
    pushBot(promptFor('AI_FALLBACK_TO_STEPS'));
    goTo(getFallbackStep(fallbackDraft));
  };

  /** Nút "Dừng, chuyển sang hỏi từng bước" — chủ động, không cần đợi AI reject/exhausted. */
  const handleStopAiConverse = () => {
    pushUser('Dừng, chuyển sang hỏi từng bước');
    fallbackToSteps();
  };

  /**
   * AI_CONVERSE: gửi 1 lượt hội thoại tự do lên `/api/chatbot/converse`, xử
   * lý đúng 5 action có thể nhận về. `ready`/`ask`/`reject` đến từ AI;
   * `exhausted`/`unavailable` do backend tự quyết (hết lượt hoặc AI lỗi/tắt)
   * — cả hai đều rơi về luồng hỏi-từng-bước qua `fallbackToSteps`.
   */
  const handleSubmitAiTurn = async () => {
    if (textInput.trim().length === 0) return;
    const userText = textInput.trim();
    pushUser(userText);
    setTextInput('');

    const turns: AiTurn[] = [...draft.aiTurns, { role: 'user', text: userText }];
    const currentDraft = {
      title: draft.refinedTitle ?? (draft.rawTitle || null),
      description: draft.refinedDescription ?? (draft.rawDescription || null),
      project_id: draft.projectId,
    };

    setBotTyping(true);
    const result = await aiConversation.send(turns, currentDraft, draft.aiTurnsUsed);
    setBotTyping(false);

    if (result.action === 'exhausted' || result.action === 'unavailable') {
      // Giữ lại title/description AI đã thu được trước khi rơi về luồng
      // từng bước — không bắt user nhập lại thứ đã nói (xem spec AI v2).
      fallbackToSteps({
        rawTitle: result.draft.title ?? draft.rawTitle,
        refinedTitle: result.draft.title ?? draft.refinedTitle,
        rawDescription: result.draft.description ?? draft.rawDescription,
        refinedDescription: result.draft.description ?? draft.refinedDescription,
        projectId: result.draft.project_id ?? draft.projectId,
      });
      return;
    }

    const nextTurnsUsed = result.action === 'reject' ? draft.aiTurnsUsed : draft.aiTurnsUsed + 1;
    const updatedTurns: AiTurn[] = [...turns, { role: 'bot', text: result.message }];

    setDraft((prev) => ({
      ...prev,
      aiTurns: updatedTurns,
      aiTurnsUsed: nextTurnsUsed,
      rawTitle: result.draft.title ?? prev.rawTitle,
      refinedTitle: result.draft.title ?? prev.refinedTitle,
      rawDescription: result.draft.description ?? prev.rawDescription,
      refinedDescription: result.draft.description ?? prev.refinedDescription,
      projectId: result.draft.project_id ?? prev.projectId,
    }));
    pushBot(result.message);

    if (result.action === 'ready') {
      goTo(getNextStep('AI_CONVERSE', draft));
    }
    // 'ask' và 'reject': ở lại AI_CONVERSE, chờ lượt tiếp theo của user.
  };

  /** Nút gửi / phím Enter dùng chung cho ba bước nhập chữ. */
  const handleSendText = () => {
    if (step === 'ASK_TITLE') handleSubmitTitle();
    else if (step === 'ASK_DESCRIPTION') handleSubmitDescription();
    else if (step === 'AI_CONVERSE') void handleSubmitAiTurn();
  };

  const handleInputKeyDown = (e: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Enter gửi, Shift+Enter xuống dòng — quy ước quen thuộc của ứng dụng chat.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  const handleSelectProject = (projectId: string | null, label: string) => {
    pushUser(label);
    const next = { ...draft, projectId, phaseId: null };
    setDraft(next);
    const nextStep = getNextStep('SELECT_PROJECT', next);
    pushBotDelayed(nextStep === 'SELECT_PHASE' ? promptFor('ASK_PHASE') : promptFor('ASK_IMAGES'), () =>
      goTo(nextStep),
    );
  };

  const handleSelectPhase = (phaseId: string | null, label: string) => {
    pushUser(label);
    const next = { ...draft, phaseId };
    setDraft(next);
    pushBotDelayed(promptFor('ASK_IMAGES'), () => goTo(getNextStep('SELECT_PHASE', next)));
  };

  /** Đợi tối đa REFINE_WAIT_TIMEOUT_MS cho một refine promise đang chạy; hết giờ thì dùng fallback. */
  const awaitWithTimeout = async (promise: Promise<string> | null, fallback: string): Promise<string> => {
    if (!promise) return fallback;
    const timeout = new Promise<string>((resolve) => setTimeout(() => resolve(fallback), REFINE_WAIT_TIMEOUT_MS));
    return Promise.race([promise, timeout]);
  };

  /**
   * Điểm vào DUY NHẤT để chuyển sang CONFIRM — cả nút "Tiếp tục" (có ảnh) lẫn
   * "Không có ảnh" đều phải gọi hàm này, không được nhảy thẳng sang CONFIRM,
   * nếu không sẽ bỏ sót kết quả refine đang chạy nền (title/description sẽ
   * hiện bản thô thay vì bản đã làm sạch).
   */
  const handleEnterConfirm = async (userMessage: string) => {
    pushUser(userMessage);
    setConfirmingEntry(true);
    try {
      const [refinedTitle, refinedDescription] = await Promise.all([
        awaitWithTimeout(titleRefinePromise.current, draft.rawTitle),
        awaitWithTimeout(descriptionRefinePromise.current, draft.rawDescription),
      ]);
      setDraft((prev) => ({ ...prev, refinedTitle, refinedDescription }));
      pushBotDelayed(promptFor('CONFIRM_INTRO'), () => goTo('CONFIRM'));
    } finally {
      setConfirmingEntry(false);
    }
  };

  const handleEditTitle = () => {
    setEditValue(draft.refinedTitle ?? draft.rawTitle);
    setEditingField('title');
  };

  const handleEditDescription = () => {
    setEditValue(draft.refinedDescription ?? draft.rawDescription);
    setEditingField('description');
  };

  const handleEditProject = () => {
    setEditingField('project');
  };

  const handleCancelEdit = () => setEditingField(null);

  const handleSaveTitle = () => {
    const trimmed = editValue.trim();
    if (trimmed.length === 0) return;
    // Người dùng tự gõ lại tại đây coi như bản cuối — không cần refine nữa.
    setDraft((prev) => ({ ...prev, rawTitle: trimmed, refinedTitle: null }));
    setEditingField(null);
  };

  const handleSaveDescription = () => {
    const trimmed = editValue.trim();
    if (trimmed.length === 0) return;
    setDraft((prev) => ({ ...prev, rawDescription: trimmed, refinedDescription: null }));
    setEditingField(null);
  };

  const handleSelectProjectInline = (projectId: string | null) => {
    setDraft((prev) => ({ ...prev, projectId, phaseId: null }));
    setEditingField(null);
  };

  const handleConfirm = async () => {
    pushUser('Xác nhận tạo yêu cầu');
    setSubmitting(true);
    try {
      const created = await featureRequestApi.create({
        title: draft.refinedTitle ?? draft.rawTitle,
        description: draft.refinedDescription ?? draft.rawDescription,
        project_id: draft.projectId,
        phase_id: draft.phaseId,
      });
      if (draft.images.length > 0) {
        try {
          await featureRequestFileApi.upload(created.id, draft.images);
        } catch (err) {
          notify(getApiErrorMessage(err, 'Đã tạo yêu cầu nhưng upload ảnh thất bại'), 'warning');
        }
      }
      pushBotDelayed(promptFor('DONE'), () => goTo('DONE'));
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tạo yêu cầu chức năng'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => setOpen(false);

  /** "Huỷ, bắt đầu lại" — cũng tắt luôn chế độ sửa inline đang dở ở CONFIRM nếu có. */
  const handleResetAll = () => {
    setEditingField(null);
    reset();
  };

  const handleFinishAndReset = () => {
    reset();
    setMessages([]);
    setOpen(false);
  };

  /** Bấm icon "?" trên thanh tiêu đề: chuyển sang màn hình chọn chủ đề hướng dẫn. */
  const handleOpenGuide = () => {
    setWidgetMode('guide');
    setGuideTopic(null);
    setGuideStepIndex(0);
  };

  /** Bấm mũi tên quay lại ở chế độ hướng dẫn: về luồng tạo yêu cầu chức năng như cũ. */
  const handleExitGuide = () => {
    setWidgetMode('feature-request');
    setGuideTopic(null);
    setGuideStepIndex(0);
  };

  const handleSelectGuideTopic = (topic: GuideTopic) => {
    setGuideTopic(topic);
    setGuideStepIndex(0);
  };

  /** "Quay lại" ở bước đầu tiên của một chủ đề quay về màn hình chọn chủ đề. */
  const handleGuidePrev = () => {
    if (guideStepIndex === 0) {
      setGuideTopic(null);
      return;
    }
    setGuideStepIndex((i) => i - 1);
  };

  const handleGuideNext = () => {
    if (!guideTopic) return;
    const total = GUIDE_TOPIC_STEPS[guideTopic].length;
    if (guideStepIndex >= total - 1) {
      // Đã xem hết các bước — về màn hình chọn chủ đề để xem hướng dẫn khác.
      setGuideTopic(null);
      setGuideStepIndex(0);
      return;
    }
    setGuideStepIndex((i) => i + 1);
  };

  const windowPosition = windowPositionFor(fabPosition);
  const isTextStep = (TEXT_INPUT_STEPS as readonly string[]).includes(step);
  const projectName = projects.find((p) => p.id === draft.projectId)?.name ?? 'Không liên quan dự án';
  const phaseName = draft.phaseId ? (phases.find((p) => p.id === draft.phaseId)?.name ?? '') : '';

  /** Nút lựa chọn nhanh dạng viên thuốc, hiện ngay dưới tin nhắn của bot. */
  const quickReplySx = {
    borderRadius: 999,
    textTransform: 'none',
    px: 2,
    py: 0.5,
    bgcolor: 'background.paper',
    borderColor: 'primary.light',
    fontWeight: 500,
    '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
  } as const;

  return (
    <>
      <Fab
        color="primary"
        aria-label={open ? 'Thu gọn chatbot' : 'Mở chatbot hỗ trợ'}
        onMouseDown={handleDragStart}
        onClick={handleFabClick}
        sx={{
          position: 'fixed',
          top: fabPosition.y,
          left: fabPosition.x,
          width: FAB_SIZE,
          height: FAB_SIZE,
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          zIndex: (theme) => theme.zIndex.tooltip,
        }}
      >
        {open ? <CloseIcon /> : <SmartToyIcon />}
      </Fab>

      {open && (
        <Paper
          elevation={10}
          sx={{
            position: 'fixed',
            top: windowPosition.y,
            left: windowPosition.x,
            width: WINDOW_WIDTH,
            height: WINDOW_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            zIndex: (theme) => theme.zIndex.tooltip,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* Thanh tiêu đề — cũng là tay cầm để kéo cửa sổ đi chỗ khác. */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            onMouseDown={handleDragStart}
            sx={{
              px: 2,
              py: 1.5,
              cursor: 'move',
              userSelect: 'none',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'primary.contrastText',
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar sx={{ width: 30, height: 30, bgcolor: 'rgba(255,255,255,0.22)' }}>
                <SmartToyIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
                  Hỗ trợ {BOT_NAME}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {widgetMode === 'guide' ? 'Chế độ hướng dẫn' : 'Thường trả lời ngay'}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5} onMouseDown={(e) => e.stopPropagation()}>
              {widgetMode === 'guide' ? (
                <Tooltip title="Quay lại trò chuyện">
                  <IconButton
                    onClick={handleExitGuide}
                    aria-label="Quay lại trò chuyện"
                    size="small"
                    sx={{ color: 'inherit' }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Hướng dẫn sử dụng">
                  <IconButton
                    onClick={handleOpenGuide}
                    aria-label="Hướng dẫn sử dụng"
                    size="small"
                    sx={{ color: 'inherit' }}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton onClick={handleClose} aria-label="Đóng chatbot" size="small" sx={{ color: 'inherit' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          {/* Lịch sử hội thoại + lựa chọn của bước hiện tại — cùng cuộn trong một luồng chat. */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              px: 2,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              bgcolor: '#F5F7FB',
            }}
          >
            {widgetMode === 'guide' ? (
              <GuideBody
                topic={guideTopic}
                stepIndex={guideStepIndex}
                onSelectTopic={handleSelectGuideTopic}
                onPrev={handleGuidePrev}
                onNext={handleGuideNext}
              />
            ) : (
              <>
                {!config && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {messages.map((m) => {
              const isUser = m.role === 'user';
              return (
                <Box
                  key={m.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isUser ? 'flex-end' : 'flex-start',
                    animation: `${messageInAnimation} 0.18s ease-out`,
                  }}
                >
                  {/* Avatar + tên người gửi, để phân biệt rõ hai phía. */}
                  <Stack
                    direction={isUser ? 'row-reverse' : 'row'}
                    spacing={0.75}
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <Avatar
                      sx={{
                        width: 22,
                        height: 22,
                        fontSize: 11,
                        bgcolor: isUser ? 'secondary.main' : 'primary.main',
                      }}
                    >
                      {isUser ? (user?.name?.charAt(0).toUpperCase() ?? 'B') : <SmartToyIcon sx={{ fontSize: 13 }} />}
                    </Avatar>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {isUser ? 'Bạn' : BOT_NAME}
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      px: 1.75,
                      py: 1.25,
                      maxWidth: '85%',
                      bgcolor: isUser ? 'primary.main' : 'background.paper',
                      color: isUser ? 'primary.contrastText' : 'text.primary',
                      borderRadius: 2.5,
                      borderTopRightRadius: isUser ? 4 : 20,
                      borderTopLeftRadius: isUser ? 20 : 4,
                      boxShadow: isUser ? 'none' : '0 1px 2px rgba(16,24,40,0.08)',
                      border: isUser ? 'none' : '1px solid',
                      borderColor: isUser ? 'transparent' : 'divider',
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                      {m.text}
                    </Typography>
                  </Box>

                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25, fontSize: 10 }}>
                    {m.time}
                  </Typography>
                </Box>
              );
            })}

            {botTyping && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  animation: `${messageInAnimation} 0.18s ease-out`,
                }}
              >
                <Box
                  sx={{
                    px: 1.75,
                    py: 1.25,
                    borderRadius: 2.5,
                    borderTopLeftRadius: 4,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    gap: 0.5,
                  }}
                >
                  {[0, 1, 2].map((dot) => (
                    <Box
                      key={dot}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'text.secondary',
                        animation: `${typingBounce} 1s ease-in-out ${dot * 0.15}s infinite`,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Lựa chọn nhanh của bước hiện tại — nằm trong luồng chat, ngay dưới câu hỏi. */}
            {config && !botTyping && step === 'SELECT_SERVICE' && (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                {config.services.length === 0 && (
                  <Alert severity="info" sx={{ width: '100%' }}>
                    Hiện chưa có dịch vụ nào khả dụng.
                  </Alert>
                )}
                {config.services.map((svc) => (
                  <Button
                    key={svc.id}
                    variant="outlined"
                    size="small"
                    disabled={svc.key !== 'FEATURE_REQUEST'}
                    onClick={() => handleSelectService(svc)}
                    sx={quickReplySx}
                  >
                    {svc.label}
                    {svc.key !== 'FEATURE_REQUEST' && <Chip label="Sắp có" size="small" sx={{ ml: 0.75, height: 18 }} />}
                  </Button>
                ))}
                <Button variant="outlined" size="small" onClick={handleOpenGuide} sx={quickReplySx}>
                  Hướng dẫn sử dụng chức năng
                </Button>
              </Stack>
            )}

            {config && !botTyping && step === 'CHOOSE_MODE' && (
              <Stack direction="column" gap={1} sx={{ mt: 0.5 }}>
                {aiEnabled && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ ...quickReplySx, flexDirection: 'column', alignItems: 'flex-start', py: 0.75 }}
                    onClick={() => handleChooseMode('ai')}
                  >
                    <Typography variant="body2" fontWeight={600} component="span">
                      Trò chuyện với AI
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="span">
                      AI hỏi lại để làm rõ yêu cầu
                    </Typography>
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ ...quickReplySx, flexDirection: 'column', alignItems: 'flex-start', py: 0.75 }}
                  onClick={() => handleChooseMode('steps')}
                >
                  <Typography variant="body2" fontWeight={600} component="span">
                    Hướng dẫn tôi từng bước
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="span">
                    Trả lời từng câu, chắc chắn không sót thông tin
                  </Typography>
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ ...quickReplySx, flexDirection: 'column', alignItems: 'flex-start', py: 0.75 }}
                  onClick={handleOpenManualForm}
                >
                  <Typography variant="body2" fontWeight={600} component="span">
                    Để tôi tự mô tả
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="span">
                    Gõ thoải mái, không cần trả lời từng câu
                  </Typography>
                </Button>
              </Stack>
            )}

            {/* Cho user chủ động rời AI giữa chừng, không cần đợi AI reject/exhausted. */}
            {config && !botTyping && step === 'AI_CONVERSE' && (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                <Button variant="outlined" size="small" sx={quickReplySx} onClick={handleStopAiConverse}>
                  Dừng, chuyển sang hỏi từng bước
                </Button>
              </Stack>
            )}

            {/* Cho user bỏ cuộc ngay ở 2 bước nhập chữ đầu tiên, không phải gõ chữ nào đó để lách qua. */}
            {config && !botTyping && (step === 'ASK_TITLE' || step === 'ASK_DESCRIPTION') && (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                <Button variant="outlined" size="small" sx={quickReplySx} onClick={handleResetAll}>
                  Huỷ, bắt đầu lại
                </Button>
              </Stack>
            )}

            {config && !botTyping && step === 'SELECT_PROJECT' && (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={quickReplySx}
                  onClick={() => handleSelectProject(null, 'Không liên quan dự án')}
                >
                  Không liên quan dự án
                </Button>
                {projects.map((p) => (
                  <Button
                    key={p.id}
                    variant="outlined"
                    size="small"
                    sx={quickReplySx}
                    onClick={() => handleSelectProject(p.id, p.name)}
                  >
                    {p.name}
                  </Button>
                ))}
              </Stack>
            )}

            {config && !botTyping && step === 'SELECT_PHASE' && (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                <Button variant="outlined" size="small" sx={quickReplySx} onClick={() => handleSelectPhase(null, 'Bỏ qua')}>
                  Bỏ qua
                </Button>
                {phases.map((p) => (
                  <Button
                    key={p.id}
                    variant="outlined"
                    size="small"
                    sx={quickReplySx}
                    onClick={() => handleSelectPhase(p.id, p.name)}
                  >
                    {p.name}
                  </Button>
                ))}
              </Stack>
            )}

            {config && !botTyping && step === 'ASK_IMAGES' && (
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                <ImageUploader
                  value={draft.images}
                  onChange={(images) => setDraft((prev) => ({ ...prev, images }))}
                  compact
                  registerOpenPicker={registerOpenPicker}
                />
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <Button
                    variant="contained"
                    size="small"
                    disableElevation
                    disabled={confirmingEntry}
                    onClick={() => void handleEnterConfirm(`Đã đính kèm ${draft.images.length} ảnh`)}
                    startIcon={confirmingEntry ? <CircularProgress size={14} color="inherit" /> : undefined}
                    sx={{ borderRadius: 999, textTransform: 'none', px: 2 }}
                  >
                    Tiếp tục
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={quickReplySx}
                    disabled={confirmingEntry}
                    onClick={() => void handleEnterConfirm('Không có ảnh')}
                  >
                    Không có ảnh
                  </Button>
                </Stack>
              </Stack>
            )}

            {/* Thẻ tóm tắt trước khi gửi — dạng card giàu nội dung trong luồng chat. */}
            {config && !botTyping && step === 'CONFIRM' && (
              <Paper
                variant="outlined"
                sx={{ p: 1.75, borderRadius: 2.5, bgcolor: 'background.paper', mt: 0.5 }}
              >
                <Stack spacing={1.25}>
                  <SummaryRow
                    label="Tên yêu cầu"
                    value={draft.refinedTitle ?? draft.rawTitle}
                    onEdit={handleEditTitle}
                    editing={editingField === 'title'}
                    onCancel={handleCancelEdit}
                    onSave={handleSaveTitle}
                    editor={
                      <TextField
                        autoFocus
                        size="small"
                        fullWidth
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        inputProps={{ maxLength: 300 }}
                      />
                    }
                  />
                  <Divider />
                  <SummaryRow
                    label="Mô tả"
                    value={draft.refinedDescription ?? draft.rawDescription}
                    onEdit={handleEditDescription}
                    editing={editingField === 'description'}
                    onCancel={handleCancelEdit}
                    onSave={handleSaveDescription}
                    editor={
                      <TextField
                        autoFocus
                        size="small"
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={6}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        inputProps={{ maxLength: 5000 }}
                      />
                    }
                  />
                  <Divider />
                  <SummaryRow
                    label="Dự án"
                    value={phaseName ? `${projectName} / ${phaseName}` : projectName}
                    onEdit={handleEditProject}
                    editing={editingField === 'project'}
                    onCancel={handleCancelEdit}
                    editor={
                      <Select
                        autoFocus
                        size="small"
                        fullWidth
                        value={draft.projectId ?? ''}
                        onChange={(e) => handleSelectProjectInline(e.target.value || null)}
                      >
                        <MenuItem value="">Không liên quan dự án</MenuItem>
                        {projects.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    }
                  />
                  <Divider />
                  <SummaryRow label="Ảnh đính kèm" value={`${draft.images.length} ảnh`} />
                </Stack>
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={submitting}
                    onClick={() => void handleConfirm()}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                    sx={{ borderRadius: 999, textTransform: 'none', py: 1, fontWeight: 600 }}
                  >
                    Xác nhận tạo yêu cầu
                  </Button>
                  <Button
                    size="small"
                    disabled={submitting}
                    onClick={handleResetAll}
                    sx={{ textTransform: 'none' }}
                  >
                    Huỷ, bắt đầu lại
                  </Button>
                </Stack>
              </Paper>
            )}

                {config && !botTyping && step === 'DONE' && (
                  <Button
                    variant="contained"
                    fullWidth
                    disableElevation
                    onClick={handleFinishAndReset}
                    sx={{ borderRadius: 999, textTransform: 'none', py: 1, mt: 0.5, fontWeight: 600 }}
                  >
                    Đóng
                  </Button>
                )}

                <div ref={messagesEndRef} />
              </>
            )}
          </Box>

          {/* Thanh nhập tin nhắn — chỉ hiện ở chế độ tạo yêu cầu, ẩn hẳn ở chế độ hướng dẫn (không cần gõ chữ). */}
          {widgetMode === 'feature-request' && (
            <Box sx={{ px: 1.5, py: 1.25, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={0.5} alignItems="flex-end">
                <InputBase
                  fullWidth
                  multiline
                  maxRows={4}
                  value={textInput}
                  disabled={!isTextStep || botTyping}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  placeholder={isTextStep ? 'Nhập tin nhắn...' : 'Chọn một lựa chọn phía trên...'}
                  inputProps={{ maxLength: step === 'ASK_TITLE' ? 300 : 5000, 'aria-label': 'Nhập tin nhắn' }}
                  sx={{
                    px: 1.75,
                    py: 0.75,
                    borderRadius: 999,
                    bgcolor: isTextStep ? '#F0F2F7' : 'transparent',
                    fontSize: 14,
                  }}
                />
                {step === 'ASK_IMAGES' && (
                  <Tooltip title="Đính kèm ảnh">
                    <IconButton
                      aria-label="Đính kèm ảnh"
                      color="primary"
                      onClick={() => openImagePickerRef.current?.()}
                    >
                      <AttachFileIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton
                  aria-label="Gửi"
                  color="primary"
                  disabled={!isTextStep || textInput.trim().length === 0 || botTyping}
                  onClick={handleSendText}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          )}
        </Paper>
      )}

      <RequestFormDialog
        open={manualFormOpen}
        request={null}
        onClose={() => setManualFormOpen(false)}
        onSaved={() => {
          setManualFormOpen(false);
          reset();
        }}
      />
    </>
  );
}

/** Một dòng trong thẻ tóm tắt, kèm nút sửa nhanh quay lại đúng bước tương ứng. */
function SummaryRow({
  label,
  value,
  onEdit,
  editing,
  editor,
  onSave,
  onCancel,
}: {
  label: string;
  value: string;
  onEdit?: () => void;
  /** Bật thì hiện `editor` ngay tại chỗ thay cho `value` — không rời step, không hỏi lại qua chat. */
  editing?: boolean;
  editor?: ReactNode;
  /** Không truyền thì không hiện nút "Lưu" — dùng cho field tự chốt khi chọn (vd dropdown dự án). */
  onSave?: () => void;
  onCancel?: () => void;
}): JSX.Element {
  if (editing) {
    return (
      <Stack spacing={0.75}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        {editor}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={onCancel} sx={{ textTransform: 'none' }}>
            Huỷ
          </Button>
          {onSave && (
            <Button size="small" variant="contained" disableElevation onClick={onSave} sx={{ textTransform: 'none' }}>
              Lưu
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }
  return (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {value}
        </Typography>
      </Box>
      {onEdit && (
        <Tooltip title="Sửa">
          <IconButton size="small" aria-label={`Sửa ${label}`} onClick={onEdit}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

const guideQuickReplySx = {
  borderRadius: 999,
  textTransform: 'none',
  px: 2,
  py: 0.5,
  bgcolor: 'background.paper',
  borderColor: 'primary.light',
  fontWeight: 500,
  '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
} as const;

/**
 * Thân nội dung của chế độ "Hướng dẫn sử dụng" — hoàn toàn thuần văn bản,
 * không gắn với bất kỳ phần tử DOM nào của trang thật. `topic === null` hiện
 * danh sách chủ đề để chọn; có `topic` thì hiện từng bước của chủ đề đó, có
 * nút Quay lại/Tiếp theo để điều hướng tuần tự.
 */
function GuideBody({
  topic,
  stepIndex,
  onSelectTopic,
  onPrev,
  onNext,
}: {
  topic: GuideTopic | null;
  stepIndex: number;
  onSelectTopic: (topic: GuideTopic) => void;
  onPrev: () => void;
  onNext: () => void;
}): JSX.Element {
  // Khai báo TRƯỚC mọi return sớm bên dưới — hook phải gọi vô điều kiện ở
  // cùng vị trí mỗi lần render (Rules of Hooks).
  //
  // Cờ boolean (không lưu URL ảnh cụ thể): Dialog luôn hiển thị ảnh của
  // BƯỚC HIỆN TẠI. Nhờ vậy khi đang xem ảnh phóng to mà bấm "Tiếp theo"/
  // "Quay lại" (nằm phía sau, vẫn bấm được vì Dialog không chặn tương tác
  // nền), ảnh trong Dialog tự đổi theo ngay — không bị đứng yên ở ảnh cũ.
  const [zoomOpen, setZoomOpen] = useState(false);

  if (!topic) {
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary">
          Chọn một chủ đề để xem hướng dẫn từng bước:
        </Typography>
        {(Object.keys(GUIDE_TOPIC_OPTIONS) as GuideTopic[]).map((key) => (
          <Paper
            key={key}
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              cursor: 'pointer',
              bgcolor: 'background.paper',
              '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
            }}
            onClick={() => onSelectTopic(key)}
          >
            <Typography variant="body2" fontWeight={700}>
              {GUIDE_TOPIC_OPTIONS[key].label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {GUIDE_TOPIC_OPTIONS[key].description}
            </Typography>
          </Paper>
        ))}
      </Stack>
    );
  }

  const steps = GUIDE_TOPIC_STEPS[topic];
  const total = steps.length;
  const isLast = stepIndex >= total - 1;
  const currentStep = steps[stepIndex];

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {GUIDE_TOPIC_OPTIONS[topic].label} — Bước {stepIndex + 1}/{total}
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
          borderRadius: 2.5,
          borderTopLeftRadius: 4,
          bgcolor: 'background.paper',
        }}
      >
        {currentStep.image && (
          <Box
            onClick={() => setZoomOpen(true)}
            sx={{
              position: 'relative',
              cursor: 'zoom-in',
              '&:hover .guide-zoom-hint': { opacity: 1 },
            }}
          >
            <Box
              component="img"
              src={currentStep.image}
              alt=""
              // Ảnh minh hoạ là tài nguyên tĩnh tự chuẩn bị, có thể chưa tồn
              // tại với vài bước — ẩn hẳn nếu load lỗi thay vì hiện icon ảnh
              // vỡ (và ẩn luôn gợi ý zoom đè lên trên).
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const hint = e.currentTarget.nextElementSibling as HTMLElement | null;
                if (hint) hint.style.display = 'none';
              }}
              sx={{ display: 'block', width: '100%', maxHeight: 220, objectFit: 'contain', bgcolor: '#F0F2F7' }}
            />
            <Box
              className="guide-zoom-hint"
              sx={{
                position: 'absolute',
                bottom: 6,
                right: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 999,
                bgcolor: 'rgba(0,0,0,0.55)',
                color: 'white',
                opacity: 0,
                transition: 'opacity 0.15s ease-out',
                pointerEvents: 'none',
              }}
            >
              <ZoomInIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" sx={{ fontSize: 11 }}>
                Bấm để phóng to
              </Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ px: 1.75, py: 1.25 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {currentStep.text}
          </Typography>
        </Box>
      </Paper>
      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Button variant="outlined" size="small" sx={guideQuickReplySx} onClick={onPrev}>
          Quay lại
        </Button>
        <Button
          variant="contained"
          size="small"
          disableElevation
          sx={{ borderRadius: 999, textTransform: 'none', px: 2 }}
          onClick={onNext}
        >
          {isLast ? 'Xong, chọn chủ đề khác' : 'Tiếp theo'}
        </Button>
      </Stack>

      {/* Phóng to ảnh minh hoạ toàn màn hình — MUI Dialog tự đóng khi nhấn
          ESC hoặc bấm ra ngoài (hành vi mặc định, không cần tự bắt phím).
          Luôn hiển thị `currentStep.image` (ảnh của bước HIỆN TẠI) chứ
          không phải ảnh tại thời điểm mở, nên bấm Tiếp theo/Quay lại trong
          lúc đang zoom sẽ cập nhật ảnh ngay mà không cần đóng Dialog. */}
      <Dialog open={zoomOpen && !!currentStep.image} onClose={() => setZoomOpen(false)} maxWidth="lg">
        <Box sx={{ position: 'relative', bgcolor: 'black', lineHeight: 0 }}>
          <IconButton
            aria-label="Đóng"
            onClick={() => setZoomOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.4)' }}
          >
            <CloseIcon />
          </IconButton>
          {currentStep.image && (
            <Box
              component="img"
              src={currentStep.image}
              alt=""
              sx={{ display: 'block', maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', mx: 'auto' }}
            />
          )}
        </Box>
      </Dialog>
    </Stack>
  );
}
